import { useContext, useEffect, useRef, useState } from 'react';
import time from 'utils/time';
import useStakeholderEmailsFunctions from 'components/Dashboard/StakeHolders/StakeholderPage/useStakeholderEmailsFunctions';
import stakeholderIncludesStaffEmails from 'components/Dashboard/StakeHolders/StakeholderPage/stakeholderIncludesStaffEmails';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import { useCollapse } from 'react-collapsed';
import useEmailRecipientInfo from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/EmailRecipients/useEmailRecipientInfo';
import AdditionalRecipientInfo from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/EmailRecipients/AdditionalRecipientInfo';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import { store } from 'components/Store';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { Link, useNavigate } from 'react-router-dom';
import normalize from 'components/Dashboard/utilities/normalizeString';

const Emails = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team } = state;

  const { StakeholderInformation, sendEmailToContact, contentType, crmContact, setStakeholderEmails, emailsFunctions } =
    props;
  const [disabledButton, setDisabledButton] = useState(false);

  const { id, categories, email } = StakeholderInformation;
  const hasNoEmail = !crmContact?.email && !email;
  const includeStaff = stakeholderIncludesStaffEmails({ email, categories });
  // BioElements pre-loads the emails list to show the tab count; reuse that instance when passed.
  const localEmailsFunctions = useStakeholderEmailsFunctions();
  const { isLoading, emails, getEmails } = emailsFunctions ?? localEmailsFunctions;
  const initialCall = useRef(null);

  initialCall.current = (source) => {
    getEmails({ id, requestSource: source, contentType, includeSentToStaff: includeStaff });
  };

  const { newAgencyPlan } = useTopicOrClientWord();

  useEffect(() => {
    if (emailsFunctions) return; // parent (BioElements) already fetched the list
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Api call cancelled by the user');
    };
  }, []);

  useEffect(() => {
    setStakeholderEmails(emails);
  }, [emails]);

  const emailsWithoutDrafts = emails?.filter((item) => item.status === 'Sent');
  const [heightContainer, containerRef] = useHeightContainer();

  return (
    <div className='border-bottom'>
      <div className='px-4 pb-4'>
        <div
          data-tooltip-content={hasNoEmail ? 'This stakeholder does not have an email address' : undefined}
          data-tooltip-id={hasNoEmail ? 'general-tooltip' : undefined}
          data-tooltip-place='top'
        >
          <button
            className='text-left pointer action-state-hover general-button w-100 py-2 px-3 text-light-blue border rounded flex-centered general-disabled-button flex-centered'
            onClick={(e) => {
              setDisabledButton(true);
              sendEmailToContact({ e });
            }}
            disabled={disabledButton || hasNoEmail}
            style={{ minHeight: '51px', ...(hasNoEmail && { opacity: 0.5, pointerEvents: 'none' }) }}
          >
            <span
              className='icon-email d-none d-lg-inline-block mr-3 ml-lg-1 mr-lg-2 paragraph-p3 spacing-icon-email'
              style={{ marginTop: '1px' }}
            />
            <span>
              <span className='mb-0 ml-lg-2'>
                Add this {crmContact ? 'contact' : 'stakeholder'} to an email campaign
              </span>
            </span>
          </button>
        </div>
      </div>
      <div className='border-top pt-2 pb-4' ref={containerRef} style={{ minHeight: `${heightContainer - 1}px` }}>
        {isLoading ? (
          <LoadingStateEmails />
        ) : (
          <>
            {emailsWithoutDrafts?.length > 0 ? (
              <>
                {emailsWithoutDrafts.map((item) => {
                  const { crmEmailId } = item;
                  return (
                    <EmailItem
                      StakeholderInformation={StakeholderInformation}
                      includeStaff={includeStaff}
                      key={`item-${crmEmailId}`}
                      item={item}
                    />
                  );
                })}
              </>
            ) : (
              <div
                className='flex-centered justify-content-center text-center'
                style={{ height: `${heightContainer - 36}px` }}
              >
                <p className='main-light-text title-h5-m' style={{ maxWidth: '420px' }}>
                  {newAgencyPlan
                    ? `Emails your team sends to this stakeholder on behalf of ${team?.teamName} will appear here`
                    : `You haven’t sent any emails to this stakeholder${includeStaff ? `’s staff` : ''}`}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const EmailItem = (props) => {
  const { item, includeStaff, StakeholderInformation } = props;
  const { senderDisplayName, subject, crmEmailId, finishedSendingAt, sendRecords } = item;
  const { officalName } = StakeholderInformation ?? {};
  const firstName = senderDisplayName.split(' ')[0];
  const lastName = senderDisplayName.split(' ')[1];
  const [isExpanded, setIsExpanded] = useState(false);
  const { getToggleProps, getCollapseProps } = useCollapse({ isExpanded });

  const { isLoading, getRecipientInfo, recipientInfo } = useEmailRecipientInfo();
  const navigate = useNavigate();

  return (
    <div
      className={`position-relative pointer stakeholder-email-item`}
      {...getToggleProps({
        onClick: () => {
          if (!isExpanded) {
            getRecipientInfo({
              emailId: crmEmailId,
              contactId: sendRecords?.[0]?.crmContactId,
            });
          }
          setIsExpanded((prev) => !prev);
        },
      })}
    >
      <div className='stakeholder-item-title px-4 pt-3 pb-2'>
        <div className='d-flex align-items-start mb-2'>
          <img
            style={{ width: '30px', marginTop: '-1px' }}
            alt='rounded'
            className='rounded-circle img-fluid d-inline-blcok'
            src={`https://cdn.auth0.com/avatars/${firstName ? Array.from(firstName)?.[0]?.toLowerCase() : ''}${
              lastName ? Array.from(lastName)?.[0]?.toLowerCase() : ''
            }.png`}
          />
          <div className='pl-2 pr-3 flex-grow-1'>
            <div className='d-flex justify-content-between flex-grow-1'>
              <div>
                <p className={`mb-0 stakeholder-email-subject font-weight-bold`}>{subject}</p>
                <p className='mb-0' style={{ lineHeight: 1.35, marginTop: includeStaff ? '-5px' : '0px' }}>
                  <span className='stakeholder-email-description main-light-text d-inline-block'>
                    {time(finishedSendingAt).formatEmbargoedDate()}
                  </span>
                </p>
              </div>
              {includeStaff && (
                <div className='pr-1 specific-top-margin-4'>
                  <p className='mb-0 rounded-pill border light-60-text bg-blue-hovered paragraph-p3 specific-x-padding-13 specific-y-padding-2'>
                    Sent to staffer
                  </p>
                </div>
              )}
            </div>
            {includeStaff && (
              <p className='mt-1 specific-bottom-margin-4 light-60-text paragraph-p2'>
                Sent to{' '}
                <a
                  className='link-hover text-light-blue'
                  href={`/stakeholders/${sendRecords[0]?.stakeholderContactId}/${normalize(sendRecords[0]?.fullName)}`}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {sendRecords[0]?.fullName}
                </a>
                , staffer for {officalName}{' '}
                {sendRecords?.length > 1 && (
                  <Link
                    to={`/people/emails/${crmEmailId}`}
                    onClick={() => {
                      navigate(`/people/email-performance/${crmEmailId}`, {
                        state: {
                          prepopulatedActiveTab: 'Recipients',
                        },
                      });
                    }}
                  >
                    + {sendRecords?.length - 1} other{sendRecords?.length - 1 > 1 ? 's' : ''}
                  </Link>
                )}
              </p>
            )}
          </div>
        </div>

        <span className={`dropdown-item-element ${isExpanded ? `open` : ''}`} />
      </div>
      <div className='email-recipients-body pt-0' {...getCollapseProps()}>
        <div className='email-recipients-expanded-content'>
          <AdditionalRecipientInfo isLoading={isLoading} info={recipientInfo} />
        </div>
      </div>
    </div>
  );
};

const LoadingStateEmails = () => {
  return (
    <>
      {Array(2)
        .fill()
        .map((item, index) => (
          <SkeletonTheme baseColor='#fff' key={index}>
            <div className={`position-relative`}>
              <div className='px-4 pt-3 pb-2'>
                <div className='d-flex align-items-start mb-2'>
                  <Skeleton circle={true} width={30} height={30} />
                  <div className='pl-2'>
                    <Skeleton height={16} width={`350px`} />
                    <div className='mt-1'>
                      <Skeleton height={16} width={`130px`} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SkeletonTheme>
        ))}
    </>
  );
};

export default Emails;
